import registerFormOnShowOrchestrators from '../utils/registerFormOnShowOrchestrators';
import { lazyInitializeCalendarsDataForEventDeeplink } from 'owa-calendar-bootstrap-utils';
import { lazyOpenCalendarReadForm } from 'owa-calendar-read-form-lifecycle';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    BohemiaOptions,
} from 'owa-outlook-service-options';
import type { CalendarReadingPanePopoutConfig } from 'owa-calendar-reading-pane-types';
import { ClientItemId, getUserMailboxInfo, getGroupMailboxInfo } from 'owa-client-ids';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getDataFromParentWindow } from 'owa-popout-v2';
import { lazyGetEventByUIdRestService } from 'owa-calendar-services';
import { logUsage } from 'owa-analytics';
import { ReadingPaneViewType } from 'owa-calendar-helpers-types/lib/ReadingPaneViewType';
import { buildEmailAddress } from 'owa-calendar-forms-route-helpers';

export interface CalendarReadingPaneRouteParameters {
    itemId?: string;
    accountDomain?: string;
    accountAlias?: string;
    groupDomain?: string;
    groupAlias?: string;
    iCalUId?: string;
}

export async function deepLinkReadingPaneRouteHandler(
    parameters: CalendarReadingPaneRouteParameters
) {
    // Initialize calendars
    await lazyInitializeCalendarsDataForEventDeeplink.importAndExecute();

    // Register so compose will show if the user clicks Edit or Duplicate
    registerFormOnShowOrchestrators();

    const popoutConfig = await getDataFromParentWindow(parseCalendarReadingPanePopoutConfig);

    if (popoutConfig?.includeFluidOnPopout) {
        const options = getOptionsForFeature<BohemiaOptions>(OwsOptionsFeatureType.Bohemia);
        options.fluidEnabledForTenant = true;
    }

    if (tryInitializeFromReadingPaneConfig(popoutConfig)) {
        return;
    } else {
        tryInitializeFromItemId(parameters);
    }
}

function parseCalendarReadingPanePopoutConfig(json: string): CalendarReadingPanePopoutConfig {
    try {
        return JSON.parse(json) as CalendarReadingPanePopoutConfig;
    } catch {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (57,9): Type 'null' is not assignable to type 'CalendarReadingPanePopoutConfig'.
        // @ts-expect-error
        return null;
    }
}

async function tryInitializeFromItemId(parameters: CalendarReadingPaneRouteParameters) {
    let { itemId, iCalUId } = parameters;

    if (iCalUId) {
        const restCalendarEvent = await lazyGetEventByUIdRestService.importAndExecute(iCalUId);
        if (!restCalendarEvent) {
            // TODO Add a better error handling experience since currently the loading spinner will keep on spinning
            // VSO: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/119628
            return false;
        }
        logUsage(
            'GoIdDeepLinkClicked',
            { entrySource: 'deepLinkReadingPaneRouteHandler' },
            { isCore: true }
        );
        itemId = restCalendarEvent.Id;
    }

    if (!itemId) {
        return false;
    }

    // In case item Id is rest id, convert to EwsId.
    // If itemId is EWSId, then convertRestIdToEwsId returns the same id without any changes
    const ewsId = convertRestIdToEwsId(itemId);

    const clientItemId: ClientItemId = {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (91,9): Type 'string | null' is not assignable to type 'string'.
        // @ts-expect-error
        Id: ewsId,
        mailboxInfo: getMailboxInfoFromParameters(parameters),
    };

    lazyOpenCalendarReadForm.importAndExecute({
        itemId: clientItemId,
        entrySource: 'Deeplink',
        viewType: ReadingPaneViewType.DeeplinkPopOut,
        isUIdDeeplink: !!iCalUId,
    });

    return true;
}

function getMailboxInfoFromParameters(parameters: CalendarReadingPaneRouteParameters) {
    const { accountDomain, accountAlias, groupDomain, groupAlias } = parameters;

    if (groupDomain && groupAlias) {
        return getGroupMailboxInfo(buildEmailAddress(groupAlias, groupDomain));
    } else if (accountDomain && accountAlias) {
        return getUserMailboxInfo(buildEmailAddress(accountAlias, accountDomain));
    }

    return getUserMailboxInfo();
}

function tryInitializeFromReadingPaneConfig(popoutConfig: CalendarReadingPanePopoutConfig | null) {
    if (!popoutConfig?.readingPaneConfig) {
        return false;
    }

    const { readingPaneConfig } = popoutConfig;

    readingPaneConfig.viewType = ReadingPaneViewType.DeeplinkPopOut;
    lazyOpenCalendarReadForm.importAndExecute(readingPaneConfig);

    return true;
}
